/**
 * The two field shapes, shared by Input, Textarea, Select and NativeSelect so
 * a text box and a select beside it cannot drift apart.
 *
 * Like Button, the size carries the shape:
 *
 * - **Dense** (`sm` 32 px, `default` 40 px) is Admin's operator field: the
 *   control corner for its height (4 px small, 6 px default), 15 px text.
 * - **Customer** (`lg`, 48 px) is the field of Student's activation and
 *   password-recovery pages: the large control corner (8 px), 16 px text (which also keeps iOS
 *   from zooming the page on focus). Raised, it carries a light shadow.
 *
 * A disabled field is dimmed to half its opacity in both shapes, as Admin's
 * was and as checkboxes and switches are: a lighter fill alone read as an
 * enabled field once fields became gray in a card.
 *
 * Focus is the same in both shapes: the focus ring's 2 px yellow, laid over
 * the field's own 1 px border (offset -2 px) so a focused field shows one
 * 2 px edge, neither a border and an outline stacked nor a gap between them,
 * and a 4 px halo of the yellow at 25% around it, so focused reads apart from
 * filled. On an invalid field both take the danger color, as the border does,
 * so focus moved to the first error still reads as the error. The Student and Manager
 * stylesheets, which draw a ring on every focused control, leave the shared
 * fields to these classes by their `data-slot`.
 *
 * `surface` is one contrast rule for every app and both shapes: a field
 * inside a white card is inset (gray, the default, white while focused), a
 * field on the gray app canvas or a gray panel is raised (white). A gray field
 * on gray, or a white one on white, loses its edge.
 */
/** A field's focus: the yellow laid over its border and a soft halo around it; danger when invalid. */
export const fieldFocus = 'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus aria-invalid:outline-danger ' +
    'focus-visible:ring-4 focus-visible:ring-focus/25 aria-invalid:focus-visible:ring-danger/20';
/**
 * A checkbox's or switch's focus: the ring, 2 px out. Touching the control it
 * would merge into the yellow fill of the checked state.
 */
export const controlFocus = 'focus-visible:focus-ring';
/**
 * Border, text color, weight, focus, and the invalid and disabled states every
 * field shares.
 * `box-border` is Preflight's sizing, stated for Manager, which loads Tailwind
 * without it; the weight is stated because a control inherits its font, and a
 * label wrapping it may be bold.
 */
export const fieldBase = 'box-border w-full border border-border-default font-normal text-text-primary aria-invalid:border-danger ' +
    `disabled:cursor-not-allowed disabled:opacity-50 ${fieldFocus}`;
export const fieldSurface = {
    /**
     * Gray inside a white card: the field reads as inset. It turns white while
     * focused, so the field being typed in lifts out of its card, as Student's
     * sign-in and landing fields did.
     */
    inset: 'bg-surface-app focus:bg-surface-card',
    /** White on the gray app canvas or a gray panel: the field reads as raised. */
    raised: 'bg-surface-card',
};
const dense = 'text-body placeholder:text-text-secondary';
// No color transition: an error border and outline appear at once, not
// faded in after focus has moved to the field. The text stays 16 px, the one
// size outside the type roles: iOS Safari zooms the page into a field whose
// text is smaller when it takes focus, and these fields serve phones.
const customer = 'rounded-token-input-lg px-4 text-base placeholder:text-text-faint';
/**
 * Side padding, corner and type per size. Heights are set by each control,
 * and a single-line control takes no vertical padding: its fixed height
 * centres the 24 px line. Padding top and bottom pushed the line down, since
 * line and padding together outgrew the 32 and 40 px heights. A text area
 * adds its own (`textareaPadding`).
 */
export const fieldSize = {
    sm: `min-h-8 px-2 rounded-token-input-sm ${dense}`,
    default: `min-h-10 px-3 rounded-token-input ${dense}`,
    lg: `min-h-12 ${customer}`,
};
/** A text area's vertical padding per size, for its several lines. */
export const textareaPadding = { sm: 'py-1.5', default: 'py-2', lg: 'py-3' };
/** A raised customer field stands on the canvas with a light shadow. */
export const fieldCompounds = [{ size: 'lg', surface: 'raised', class: 'shadow-raised' }];
/**
 * The fixed height of a single-line control at each size. On a touch screen
 * the dense sizes grow to the 44 px touch target (design tokens §7).
 */
export const fieldHeight = {
    sm: 'h-8 pointer-coarse:h-11',
    default: 'h-10 pointer-coarse:h-11',
    lg: 'h-12',
};
//# sourceMappingURL=field-shape.js.map